import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { 
  getEmployeeUnviewedRecordsCount, 
  getEmployeeNewSharesCount,
  getEmployerUnviewedPendingCount,
  getEmployerUnviewedAccessCount 
} from '../utils/badgeCountUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationContext = createContext({
  // Notifications
  notifications: [],
  unreadNotificationCount: 0,
  // Company counts
  unviewedSharesCount: 0,
  pendingRequestsCount: 0,
  pendingUsersCount: 0,
  // Employee counts
  unviewedPendingCount: 0,
  unviewedAccessCount: 0,
  // Actions
  refreshCounts: () => {},
  markNotificationRead: () => {},
  markAllNotificationsRead: () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  // Get user from AuthContext
  const { user } = useAuth();
  
  // NOTE: We no longer initialize from localStorage to avoid stale cache issues
  // The counts will be fetched fresh from the API on mount
  // This prevents showing incorrect badge counts (e.g., showing 23 when only 1 unviewed)
  
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  
  // Company counts - start at 0, will be populated from API
  const [unviewedSharesCount, setUnviewedSharesCount] = useState(0);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  
  // Employee counts - start at 0, will be populated from API
  const [unviewedPendingCount, setUnviewedPendingCount] = useState(0);
  const [unviewedAccessCount, setUnviewedAccessCount] = useState(0);
  
  const [userType, setUserType] = useState(null);
  
  // Track if initial fetch has completed
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  // Clear stale localStorage cache on mount and when user changes
  // This prevents showing incorrect badge counts from previous sessions
  useEffect(() => {
    // Clear the old localStorage cache to prevent stale data
    localStorage.removeItem('notificationCounts');
  }, [user?.id]);

  const fetchCounts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || !user) return;

    const currentUserType = user.user_type;
    if (!currentUserType) return; // Can't fetch without user type
    
    setUserType(currentUserType);

    try {
      // Fetch notifications
      const notifResponse = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both array format and {notifications: [...]} format
      const notifData = notifResponse.data;
      const notifs = Array.isArray(notifData) ? notifData : (notifData.notifications || []);
      setNotifications(notifs);
      setUnreadNotificationCount(notifs.filter(n => !n.read).length);

      // Fetch dashboard counts based on user type
      if (currentUserType === 'company') {
        const dashResponse = await axios.get(`${API_URL}/api/company/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const data = dashResponse.data;
        // Calculate counts from actual records using centralized utility
        const profileAccessItems = data.profile_access || [];
        const pendingVerifications = data.pending_verifications || [];
        
        setUnviewedSharesCount(getEmployerUnviewedAccessCount(profileAccessItems));
        setPendingRequestsCount(getEmployerUnviewedPendingCount(pendingVerifications));
        
        // Fetch company users for pending invites count
        try {
          const usersResponse = await axios.get(`${API_URL}/api/company/users`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const users = usersResponse.data || [];
          const pendingInvites = users.filter(u => u.status === 'pending').length;
          setPendingUsersCount(pendingInvites);
        } catch (e) {
          setPendingUsersCount(0);
        }
        
      } else if (currentUserType === 'employee') {
        const dashResponse = await axios.get(`${API_URL}/api/employee/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const data = dashResponse.data;
        // Calculate counts from actual records using centralized utility
        const records = data.employment_records || [];
        const profileAccessItems = data.profile_access || [];
        
        setUnviewedPendingCount(getEmployeeUnviewedRecordsCount(records));
        setUnviewedAccessCount(getEmployeeNewSharesCount(profileAccessItems));
      }
    } catch (error) {
      console.error('Failed to fetch notification counts:', error);
    }
  }, [user]);

  // Initial fetch on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchCounts();
    }
  }, [fetchCounts, user]);

  // Refresh counts periodically (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  const markNotificationRead = async (notificationId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await axios.put(
        `${API_URL}/api/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await axios.put(
        `${API_URL}/api/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadNotificationCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const value = {
    notifications,
    unreadNotificationCount,
    unviewedSharesCount,
    pendingRequestsCount,
    pendingUsersCount,
    unviewedPendingCount,
    unviewedAccessCount,
    refreshCounts: fetchCounts,
    markNotificationRead,
    markAllNotificationsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
